import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import InputSelect from "../../../components/InputSelect";
import InputDate from "../../../components/InputDate";
import InputTextarea from "../../../components/InputTextarea";
import SelectCompany from "../../../components/selects/SelectCompany";
import PaymentConditionFields from "../../../components/PaymentCondition";
import { emptyPayment, paymentFromResource, paymentPayload } from "../../../components/PaymentCondition/helpers";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { convertIntToMoney, convertMoenyToInt } from "../../../helpers/conversions";
import { PURCHASE_ORDER_KINDS, DELIVERY_TARGETS } from "../helpers";
import { STATE_OPTIONS } from "../../Fiscal/helpers";
import ModalLinkOrder, { orderCustomerName } from "../ModalLinkOrder";

const emptyItem = () => ({
    key: Math.random().toString(36).slice(2),
    orderItemId: null,
    orderItemCustomizationId: null,
    description: '',
    reference: '',
    quantity: '1',
    unitCost: 'R$ 0,00',
});

const itemFromResource = item => ({
    key: item.id,
    // Vínculo com o item do pedido de venda: a edição recria os itens no
    // backend, então ele precisa voltar no payload para não se perder.
    orderItemId: item.order_item_id ?? null,
    orderItemCustomizationId: item.order_item_customization_id ?? null,
    description: item.description ?? '',
    reference: item.reference ?? '',
    quantity: String(item.quantity ?? 1),
    unitCost: convertIntToMoney(item.unit_cost ?? 0, true),
});

/**
 * Cadastro manual do pedido de compra: compra que não nasceu de pedido de
 * venda (reposição de estoque, material, serviço) ou ajuste do que a
 * automação gerou.
 */
export const ModalPurchaseOrderForm = ({ show, close, purchaseOrder, onSaved }) => {
    const [saving, setSaving] = useState(false);
    const [showLinkOrder, setShowLinkOrder] = useState(false);

    const [companyId, setCompanyId] = useState(null);
    const [linkedOrder, setLinkedOrder] = useState(null);
    const [kind, setKind] = useState('product');
    const [payment, setPayment] = useState(emptyPayment);
    // Remonta o campo de condição a cada abertura, já com o PC carregado —
    // ele guarda o texto livre do documento na montagem.
    const [paymentFieldKey, setPaymentFieldKey] = useState(0);
    const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
    const [notes, setNotes] = useState('');

    const [deliveryTarget, setDeliveryTarget] = useState('company');
    const [deliveryName, setDeliveryName] = useState('');
    const [deliveryZipCode, setDeliveryZipCode] = useState('');
    const [deliveryStreet, setDeliveryStreet] = useState('');
    const [deliveryNumber, setDeliveryNumber] = useState('');
    const [deliveryComplement, setDeliveryComplement] = useState('');
    const [deliveryDistrict, setDeliveryDistrict] = useState('');
    const [deliveryCity, setDeliveryCity] = useState('');
    const [deliveryState, setDeliveryState] = useState('');

    const [items, setItems] = useState([emptyItem()]);

    // Só busca na ViaCEP o CEP que a pessoa digitou: carregar um PC salvo não
    // pode sobrescrever o endereço de entrega que já está lá.
    const zipCodeTyped = useRef(false);

    const isEdit = !!purchaseOrder?.id;

    useEffect(() => {
        if (!show) return;

        setCompanyId(purchaseOrder?.company_id ?? null);
        setLinkedOrder(purchaseOrder?.order ?? null);
        setKind(purchaseOrder?.kind ?? 'product');
        setPayment(purchaseOrder ? paymentFromResource(purchaseOrder) : emptyPayment);
        setPaymentFieldKey(current => current + 1);
        setExpectedDeliveryDate(purchaseOrder?.expected_delivery_date?.substring(0, 10) ?? '');
        setNotes(purchaseOrder?.notes ?? '');

        setDeliveryTarget(purchaseOrder?.delivery_target ?? 'company');
        setDeliveryName(purchaseOrder?.delivery_name ?? '');
        setDeliveryZipCode(purchaseOrder?.delivery_zip_code ?? '');
        setDeliveryStreet(purchaseOrder?.delivery_street ?? '');
        setDeliveryNumber(purchaseOrder?.delivery_number ?? '');
        setDeliveryComplement(purchaseOrder?.delivery_complement ?? '');
        setDeliveryDistrict(purchaseOrder?.delivery_district ?? '');
        setDeliveryCity(purchaseOrder?.delivery_city ?? '');
        setDeliveryState(purchaseOrder?.delivery_state ?? '');

        const resourceItems = purchaseOrder?.items ?? [];
        setItems(resourceItems.length > 0 ? resourceItems.map(itemFromResource) : [emptyItem()]);

        zipCodeTyped.current = false;
    }, [show, purchaseOrder]);

    useEffect(() => {
        if (!zipCodeTyped.current) return;

        const digits = deliveryZipCode.replace(/\D/g, '');
        if (digits.length !== 8) return;

        axios.get(`https://viacep.com.br/ws/${digits}/json/`).then(res => {
            if (res.data?.erro) return;

            setDeliveryStreet(res.data.logradouro ?? '');
            setDeliveryDistrict(res.data.bairro ?? '');
            setDeliveryCity(res.data.localidade ?? '');
            setDeliveryState(res.data.uf ?? '');
        }).catch(() => {
            // CEP inexistente ou ViaCEP fora do ar: o endereço continua
            // editável à mão, não há o que avisar.
        });
    }, [deliveryZipCode]);

    const updateItem = (key, changes) => {
        setItems(current => current.map(item => (item.key === key ? { ...item, ...changes } : item)));
    };

    const removeItem = key => {
        setItems(current => (current.length === 1 ? [emptyItem()] : current.filter(item => item.key !== key)));
    };

    const filledItems = items.filter(item => item.description.trim() !== '');

    const total = filledItems.reduce((sum, item) => (
        sum + (convertMoenyToInt(item.unitCost) || 0) * (parseInt(item.quantity, 10) || 0)
    ), 0);

    const save = () => {
        if (!companyId) {
            toast.error('Escolha o fornecedor');
            return;
        }

        if (filledItems.length === 0) {
            toast.error('Adicione pelo menos um item');
            return;
        }

        setSaving(true);

        const data = {
            company_id: companyId,
            order_id: linkedOrder?.id ?? null,
            kind,
            ...paymentPayload(payment),
            expected_delivery_date: expectedDeliveryDate || null,
            notes: notes || null,
            delivery_target: deliveryTarget,
            delivery_name: deliveryName || null,
            delivery_zip_code: deliveryZipCode || null,
            delivery_street: deliveryStreet || null,
            delivery_number: deliveryNumber || null,
            delivery_complement: deliveryComplement || null,
            delivery_district: deliveryDistrict || null,
            delivery_city: deliveryCity || null,
            delivery_state: deliveryState || null,
            items: filledItems.map(item => ({
                order_item_id: item.orderItemId,
                order_item_customization_id: item.orderItemCustomizationId,
                description: item.description,
                reference: item.reference || null,
                quantity: parseInt(item.quantity, 10) || 1,
                unit_cost: convertMoenyToInt(item.unitCost) || 0,
            })),
        };

        const request = isEdit
            ? api.put(`/purchase-order/${purchaseOrder.id}`, data)
            : api.post(`/purchase-order`, data);

        request.then(() => {
            toast.success(isEdit ? 'Pedido de compra atualizado' : 'Pedido de compra criado');
            close();
            onSaved();
        }).catch(renderError).then(() => setSaving(false));
    };

    const itemCount = filledItems.length;

    return (
        <Modal
            className={`purchase-order-form-modal`}
            size={`xlarge`}
            title={isEdit ? `Editar pedido de compra #${purchaseOrder.code}` : `Novo pedido de compra`}
            show={show}
            close={close}
            footer={
                <div className={`po-modal-footer`}>
                    <span className={`footer-summary`}>
                        {itemCount === 0
                            ? 'Nenhum item'
                            : `${itemCount} ${itemCount === 1 ? 'item' : 'itens'} · ${convertIntToMoney(total, true)}`
                        }
                    </span>
                    <span className={`footer-actions`}>
                        <Button type={`secondary`} size={`small`} text={`Cancelar`} action={close} />
                        <Button type={`primary`} size={`small`} svg={`save-white`} text={`Salvar`} action={save} loading={saving} />
                    </span>
                </div>
            }
        >
            {/* Duas colunas no lugar de campos espremidos em linhas de quatro:
                o cabeçalho da compra à esquerda, os itens à direita — mesma
                divisão do modal de lançamento do financeiro. */}
            <div className={`po-modal-body`}>
                <div className={`po-main`}>
                    <span className={`po-form-section`}>Dados da compra</span>

                    <div className="row">
                        <div className="col-12">
                            <SelectCompany
                                label={`Fornecedor`}
                                selectedCompany={companyId}
                                setSelectedCompany={setCompanyId}
                                create={{ type: [kind], level: 1 }}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <InputSelect label={`Tipo`} options={PURCHASE_ORDER_KINDS} value={kind} change={setKind} />
                        </div>
                    </div>

                    <div className={`po-linked-order-field`}>
                        <label>Pedido de venda</label>
                        {linkedOrder
                            ? <div className={`po-linked-order`}>
                                <div className={`po-linked-order-info`}>
                                    <strong>Pedido #{linkedOrder.code}</strong>
                                    <span>
                                        {[
                                            orderCustomerName(linkedOrder),
                                            linkedOrder.created_at ? moment(linkedOrder.created_at).format('DD/MM/YYYY') : null,
                                        ].filter(Boolean).join(' · ')}
                                    </span>
                                </div>
                                <button type="button" onClick={() => setShowLinkOrder(true)}>Trocar</button>
                                <button type="button" onClick={() => setLinkedOrder(null)}>Remover</button>
                            </div>
                            : <button type="button" className={`po-link-order`} onClick={() => setShowLinkOrder(true)}>
                                + Vincular pedido de venda
                            </button>
                        }
                    </div>

                    <div className="row">
                        {show &&
                            <PaymentConditionFields
                                key={paymentFieldKey}
                                value={payment}
                                change={setPayment}
                                usage={`purchase`}
                                companyId={companyId}
                                termClassName={`col-6`}
                                methodClassName={`col-6`}
                                menuInBody
                                modalLevel={1}
                            />
                        }
                    </div>

                    <div className="row">
                        <div className="col-6">
                            <InputDate label={`Previsão de entrega`} format={`YYYY-MM-DD`} value={expectedDeliveryDate} change={setExpectedDeliveryDate} />
                        </div>
                        <div className="col-6">
                            <InputSelect label={`Entregar em`} options={DELIVERY_TARGETS} value={deliveryTarget} change={setDeliveryTarget} />
                        </div>
                    </div>

                    {/* No ramo de brindes a mercadoria costuma ir do fornecedor
                        direto para o gravador — por isso o endereço é do PC e
                        não do pedido de venda. */}
                    <span className={`po-form-section`}>Endereço de entrega</span>

                    <div className="row">
                        <div className="col-12">
                            <Input label={`Nome de quem recebe`} value={deliveryName} change={setDeliveryName} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-5">
                            <Input
                                label={`CEP`}
                                value={deliveryZipCode}
                                change={value => { zipCodeTyped.current = true; setDeliveryZipCode(value); }}
                                mask={`convertToCep`}
                            />
                        </div>
                        <div className="col-7">
                            <Input label={`Endereço`} value={deliveryStreet} change={setDeliveryStreet} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-4">
                            <Input label={`Número`} value={deliveryNumber} change={setDeliveryNumber} />
                        </div>
                        <div className="col-8">
                            <Input label={`Complemento`} value={deliveryComplement} change={setDeliveryComplement} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <Input label={`Bairro`} value={deliveryDistrict} change={setDeliveryDistrict} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-8">
                            <Input label={`Cidade`} value={deliveryCity} change={setDeliveryCity} />
                        </div>
                        <div className="col-4">
                            <InputSelect
                                menuInBody
                                label={`UF`}
                                options={STATE_OPTIONS}
                                value={deliveryState}
                                change={setDeliveryState}
                            />
                        </div>
                    </div>
                </div>

                <div className={`po-side`}>
                    <span className={`po-form-section`}>Itens</span>

                    {/* Os rótulos ficam uma vez só, no topo: repetidos em cada
                        linha viravam a maior parte do texto da lista. */}
                    <div className={`po-item-labels`}>
                        <span>Descrição</span>
                        <span>Referência</span>
                        <span>Qtd.</span>
                        <span>Custo unit.</span>
                        <span>Total</span>
                        <span />
                    </div>

                    {items.map(item => (
                        <div className={`po-item-row`} key={item.key}>
                            <div>
                                <Input
                                    placeholder={`O que está sendo comprado`}
                                    value={item.description}
                                    change={value => updateItem(item.key, { description: value })}
                                />
                            </div>
                            <div>
                                <Input
                                    placeholder={`Código`}
                                    value={item.reference}
                                    change={value => updateItem(item.key, { reference: value })}
                                />
                            </div>
                            <div>
                                <Input
                                    value={item.quantity}
                                    change={value => updateItem(item.key, { quantity: value.replace(/\D/g, '') })}
                                />
                            </div>
                            <div>
                                <Input
                                    value={item.unitCost}
                                    change={value => updateItem(item.key, { unitCost: value })}
                                    mask={`convertToMoney`}
                                />
                            </div>
                            <span className={`po-item-total`}>
                                {convertIntToMoney((convertMoenyToInt(item.unitCost) || 0) * (parseInt(item.quantity, 10) || 0), true)}
                            </span>
                            <Button type={`delete`} svg={`delete`} action={() => removeItem(item.key)} />
                        </div>
                    ))}

                    <button type="button" className={`po-item-add`} onClick={() => setItems(current => [...current, emptyItem()])}>
                        + Adicionar item
                    </button>

                    <InputTextarea label={`Observações`} value={notes} change={setNotes} />
                </div>
            </div>

            <ModalLinkOrder
                show={showLinkOrder}
                close={() => setShowLinkOrder(false)}
                selectedOrder={linkedOrder}
                onSelect={setLinkedOrder}
                level={1}
            />
        </Modal>
    );
};

export default ModalPurchaseOrderForm;
